import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * İŞLER, İŞ KAYITLARI ve TESLİMATLAR (M6b).
 *
 * ⚠⚠ `invoiceEntries` TASLAK ÜRETİR, fatura DÜZENLEMEZ. Düzenleme geri
 * dönüşsüzdür ve `/invoices/:id/issue` üzerinden, insan onayıyla yapılır.
 */
export type ProjectStatus = 'LEAD' | 'ACTIVE' | 'ON_HOLD' | 'DONE' | 'CANCELLED';
export type ProjectType = 'PROJECT' | 'SUBSCRIPTION' | 'SUPPORT' | 'INTERNAL';
export type ProjectBillingType = 'FIXED' | 'HOURLY' | 'SUBSCRIPTION' | 'MILESTONE';

export const WORK_ENTRY_CATEGORIES = [
  'DEVELOPMENT',
  'CONSULTING',
  'SUPPORT',
  'INSTALLATION',
  'MEETING',
  'TRAVEL',
] as const;
export type WorkEntryCategory = (typeof WORK_ENTRY_CATEGORIES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  LEAD: 'Aday',
  ACTIVE: 'Sürüyor',
  ON_HOLD: 'Beklemede',
  DONE: 'Bitti',
  CANCELLED: 'İptal',
};

export const WORK_CATEGORY_LABELS: Record<WorkEntryCategory, string> = {
  DEVELOPMENT: 'Geliştirme',
  CONSULTING: 'Danışmanlık',
  SUPPORT: 'Destek',
  INSTALLATION: 'Kurulum',
  MEETING: 'Toplantı',
  TRAVEL: 'Yol',
};

export interface Project {
  id: string;
  code: string;
  name: string;
  contactId: string | null;
  description: string | null;
  type: ProjectType;
  status: ProjectStatus;
  billingType: ProjectBillingType;
  budgetAmount: string | null;
  hourlyRate: string | null;
  defaultItemId: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface WorkEntry {
  id: string;
  projectId: string;
  workDate: string;
  description: string;
  hours: string;
  rate: string;
  costRate: string | null;
  category: WorkEntryCategory;
  billable: boolean;
  /** ⚠ Dolu ise bu iş faturalandı; boşsa faturalanmadı. TEK kaynak. */
  billedInvoiceId: string | null;
}

export interface Deliverable {
  id: string;
  name: string;
  type: 'WEBSITE' | 'KIOSK' | 'SAAS_TENANT' | 'OTHER';
  url: string | null;
  wentLiveAt: string | null;
}

export function useProjects(status?: ProjectStatus) {
  return useQuery({
    queryKey: ['projects', status ?? 'all'],
    queryFn: () => api.get<Project[]>(`/projects${status ? `?status=${status}` : ''}`),
  });
}

export function useWorkEntries(projectId: string | null, unbilledOnly = false) {
  return useQuery({
    queryKey: ['work-entries', projectId ?? 'all', unbilledOnly],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (projectId !== null) qs.set('projectId', projectId);
      if (unbilledOnly) qs.set('unbilledOnly', 'true');
      return api.get<WorkEntry[]>(`/work-entries?${qs.toString()}`);
    },
  });
}

export function useDeliverables(projectId: string | null) {
  return useQuery({
    queryKey: ['deliverables', projectId],
    enabled: projectId !== null,
    queryFn: () => api.get<Deliverable[]>(`/projects/${String(projectId)}/deliverables`),
  });
}

/**
 * ⚠ FATURALANMAMIŞ İŞ SAYACI — bir TELAFİ. İş kaydetmekle faturalamak ayrı
 * eylemler olduğu için unutulan işler sessizce birikebilir.
 */
export function useUnbilledSummary() {
  return useQuery({
    queryKey: ['projects', 'unbilled-summary'],
    queryFn: () =>
      api.get<{ entries: number; oldestDate: string | null }>('/projects/unbilled-summary'),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      code: string;
      name: string;
      contactId?: string | null;
      type?: ProjectType;
      billingType?: ProjectBillingType;
      hourlyRate?: string | null;
      defaultItemId?: string | null;
    }) => api.post<Project>('/projects', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useCreateWorkEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      projectId: string;
      workDate: string;
      description: string;
      hours: string;
      rate?: string;
      category?: WorkEntryCategory;
      billable?: boolean;
    }) => api.post<WorkEntry>('/work-entries', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-entries'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteWorkEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/work-entries/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-entries'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/** ⚠ TASLAK üretir — düzenlemez. */
export function useInvoiceWorkEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; entryIds: string[]; itemId?: string }) =>
      api.post<{ invoiceId: string; entryCount: number }>(`/projects/${input.projectId}/invoice`, {
        entryIds: input.entryIds,
        ...(input.itemId ? { itemId: input.itemId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-entries'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
